import { pxToDP, useIntl } from '@capsicum/common';
import { InteractionLayer, Text } from '@capsicum/common-web';
import { ChevronDownIcon, ChevronUpIcon } from '@capsicum/common-web/icons';
import {
	type FanTokenBreakdownCardSummaryProps,
	useFanTokenBreakdownCardSummary
} from '@capsicum/features/nc-wallet/shared';
import { type PropsWithChildren, useId } from 'react';
import styled, { useTheme } from 'styled-components';

import { V2MigrationFanTokenLogo } from '../v2ft-migration/V2MigrationFanTokenLogo';

export const FanTokenBreakdownCardSummary = ({
	isExpanded,
	toggle,
	tokens: tokensProp
}: FanTokenBreakdownCardSummaryProps) => {
	const expandID = useId();
	const { $t } = useIntl();
	const { colors } = useTheme();

	const ExpandIcon = isExpanded ? ChevronUpIcon : ChevronDownIcon;

	const { tokens } = useFanTokenBreakdownCardSummary({
		AmountIndicator: ({ children }: PropsWithChildren) => (
			<S.AmountIndicator aria-label="Additional Tokens Counter">
				<Text variant="caption1">{children}</Text>
			</S.AmountIndicator>
		),
		TeamLogo: S.StyledFanTokenBreakdownCardTokenLogo,
		tokens: tokensProp
	});

	return (
		<S.Wrapper>
			<Text variant="text1">{$t('fanTokenBreakdownCard.title')}</Text>

			<S.SummaryInfo>
				<S.SummaryInfoTokens aria-label="Tokens Summary">{tokens}</S.SummaryInfoTokens>

				<S.StyledInteractionLayer
					aria-labelledby={expandID}
					testID="fanTokenBreakdownCard-expand-button"
					onClick={toggle}
				>
					<ExpandIcon
						aria-label={
							isExpanded
								? $t('fanTokenBreakdownCard.actions.collapse')
								: $t('fanTokenBreakdownCard.actions.expand')
						}
						color={colors.icons.info.enabled}
						id={expandID}
					/>
				</S.StyledInteractionLayer>
			</S.SummaryInfo>
		</S.Wrapper>
	);
};

namespace S {
	const LOGO_OVERLAP = 4;

	export const Wrapper = styled.div`
		display: flex;
		flex-direction: row;
		align-items: center;
		justify-content: space-between;
		gap: ${({ theme }) => theme.spacings.xxsmall};
	`;

	export const SummaryInfo = styled.div`
		display: flex;
		align-items: center;
		flex-direction: row;
		gap: ${({ theme }) => pxToDP(theme.spacings.xxsmall) + LOGO_OVERLAP}px;
	`;

	export const SummaryInfoTokens = styled.div`
		display: flex;
		align-items: center;
		flex-direction: row;
	`;

	export const StyledFanTokenBreakdownCardTokenLogo = styled(V2MigrationFanTokenLogo)`
		margin-right: -${LOGO_OVERLAP}px;
	`;

	export const StyledInteractionLayer = styled(InteractionLayer)`
		user-select: none;
	`;

	export const AmountIndicator = styled.div`
		width: 22px;
		height: 22px;
		display: flex;
		align-items: center;
		justify-content: center;
		margin-right: -${LOGO_OVERLAP}px;
		${({ theme }) => theme.border.radius.circle};
		background: ${({ theme }) => theme.colors.backgrounds.main.subtle};
	`;
}
